import { IconButton } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { AP_AGING_TOTALS_COLUMNS } from "../constants"
import type { ApAgingVendor } from "../types/apAgingReport"

import { formatCurrencyWithCents } from "@/utils/currency"

export const useApAgingVendorColumns = (): ColumnDef<ApAgingVendor>[] =>
  useMemo<ColumnDef<ApAgingVendor>[]>(
    () => [
      {
        id: "vendorName",
        header: "Vendor",
        cell: ({ row }) => row.original.vendorName,
        enableSorting: false,
      },
      {
        id: "vendorNumber",
        header: "Vendor #",
        cell: ({ row }) => row.original.vendorNumber,
        enableSorting: false,
      },
      {
        id: "as400VendorNumber",
        header: "AS400 Vendor #",
        cell: ({ row }) => row.original.as400VendorNumber,
        enableSorting: false,
      },
      ...AP_AGING_TOTALS_COLUMNS.map(
        ({ key, label }): ColumnDef<ApAgingVendor> => ({
          id: key,
          header: label,
          cell: ({ row }) => {
            const amount = row.original.totals[key]
            const isTotal = key === "total"

            // The design shows a dash for a bucket without any outstanding amount; the total always shows its amount.
            if (!amount && !isTotal) return "-"

            return <span className={isTotal ? "font-bold" : undefined}>{formatCurrencyWithCents(amount)}</span>
          },
          enableSorting: false,
          meta: { align: "right" },
        })
      ),
      {
        id: "actions",
        header: "",
        size: 56,
        enableSorting: false,
        cell: ({ row }) => (
          <div className='flex items-center justify-end'>
            <IconButton
              size='small'
              aria-label={`${row.getIsExpanded() ? "Collapse" : "Expand"} ${row.original.vendorName}`}
              onClick={row.getToggleExpandedHandler()}
              data-testid={`ap-aging-report.vendor-table.expand-button-${row.original.vendorCompanyId}`}
            >
              {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </div>
        ),
      },
    ],
    []
  )
